<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { ColumnProps, ColumnSlots } from 'primevue/column';
import Column from 'primevue/column';
import { computed } from 'vue';
import { datatable } from '@shared/plugin/prime-vue/data-table';
import VTooltip from '../VTooltip/VTooltip.vue';

const props = withDefaults(
  defineProps<{
    field: ColumnProps['field']; // is opzettelijk verplicht omdat sommige developers het lastig vinden om in te schatten wanneer een value nodig is
    header?: ColumnProps['header'];
    /**
     * Toelichting bij de kolomkop, bijvoorbeeld de volledige naam achter een afkorting. De kop
     * behoudt zijn gewone opmaak; alleen bij een eigen `header`-slot doet deze prop niets.
     */
    headerTooltip?: string;
    sortable?: ColumnProps['sortable'];
    sortField?: ColumnProps['sortField'];
    rowReorder?: ColumnProps['rowReorder'];
    expander?: ColumnProps['expander'];
    class?: ColumnProps['class'];
    headerClass?: ColumnProps['headerClass'];
    frozen?: ColumnProps['frozen'];
    align?: keyof typeof columnAlign;
    pt?: ColumnProps['pt'];
    ptOptions?: ColumnProps['ptOptions'];
  }>(),
  { align: 'left' },
);

const slots = defineSlots<ColumnSlots>();

const slotNames = Object.keys(slots) as Array<keyof ColumnSlots>;

const toonHeaderTooltip = computed(() => !!props.headerTooltip && !slots.header);

const columnProps = computed(() => {
  // headerTooltip is van onszelf; PrimeVue zou hem als los attribuut op de kolom zetten. En zodra
  // wij de titel via het slot renderen, moet de prop weg — anders staat de kop er dubbel in.
  const { headerTooltip, ...rest } = props;
  return toonHeaderTooltip.value ? { ...rest, header: undefined } : rest;
});

/**
 * Dezelfde opmaak die PrimeVue's eigen titel-element krijgt. Het `header`-slot vervangt dat
 * element, dus zonder deze class zou een kolom met tooltip afwijken van de rest.
 */
const columnTitleClass = datatable.column.columnTitle.class;

const columnAlign = {
  left: '',
  center: 'justify-items-center text-center',
  right: 'justify-items-end text-right',
};
</script>

<template>
  <!-- Hotfix 'v-if="true"' until https://github.com/primefaces/primevue/issues/5360 is solved -->
  <!-- eslint-disable-next-line vue/no-constant-condition -->
  <Column v-if="true" v-bind="columnProps">
    <!--
      De tooltip komt om de titel heen in plaats van ervoor in de plaats: inline-flex houdt hem in
      de tekstregel, zodat het sorteerpijltje ernaast blijft staan en niet eronder valt.
    -->
    <template v-if="toonHeaderTooltip" #header>
      <VTooltip :label="props.headerTooltip" class="inline-flex">
        <span
          :class="[
            columnTitleClass,
            'cursor-help underline decoration-dashed decoration-from-font underline-offset-4',
          ]"
        >
          {{ props.header }}
        </span>
      </VTooltip>
    </template>

    <template #sorticon="{ class: sorticonClass, sorted, sortOrder }">
      <FontAwesomeIcon
        v-show="sorted"
        :class="[sorticonClass, 'text-sm']"
        :icon="['far', `arrow-${sortOrder > 0 ? 'down-short-wide' : 'up-wide-short'}`]"
      />
    </template>

    <template v-for="slotName in slotNames" :key="slotName" #[slotName]="slotProps">
      <div :class="columnAlign[props.align]">
        <slot :name="slotName" v-bind="slotProps as any"></slot>
      </div>
    </template>
  </Column>
</template>
